import { useState, useMemo, useEffect } from 'react';
import { Column } from 'react-table';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import ScrollX from 'components/ScrollX';
import { fetchUserList } from 'pages/user-list-page/api';
import { mapFiltersToSpecification } from 'utils/filtering/map-filter-to-specification';
import SelectUserTableColumnConfig, { DefaultVisibleUserColumns } from './recipient-select-column-config';
import { Button, Dialog, DialogTitle, DialogContent, Typography, IconButton } from '@mui/material';
import { EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { PopupTransition } from 'components/@extended/Transitions';
import UserDto from 'common/dto-types/user-dto';
import UserGroupDto from 'common/dto-types/user-group-dto';

type MessageTargetPreviewTableProps = {
    selectedUsers: UserDto[];
    userGroups: UserGroupDto[];
};

const MessageTargetPreviewTable = ({ selectedUsers, userGroups }: MessageTargetPreviewTableProps) => {
    const [dataList, setDataList] = useState<Array<any>>([]);
    const [pageCount, setPageCount] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Trigger refresh when selectedUsers or userGroups change
    useEffect(() => {
        setRefreshKey((prev) => prev + 1);
    }, [selectedUsers, userGroups]);

    // Create column config without sorting but with filters enabled
    const columnConfig: Column[] = useMemo(
        () =>
            SelectUserTableColumnConfig.map((col) => ({
                ...col,
                disableSortBy: true,
            })),
        [],
    );

    const fetchData: BaseTableFetchFuncProps = async ({ pageSize, pageIndex, filters }) => {
        // Build OR specifications: group/user targeting
        const orSpecs: string[] = [];

        // Add user group specifications (users from ANY selected group)
        if (userGroups && userGroups.length > 0) {
            const groupIds = userGroups.map((group) => group.id).join(';');
            orSpecs.push(`GroupIdsSpecification:${groupIds}`);
        }

        // Add individual user specifications (ANY individually selected user)
        if (selectedUsers && selectedUsers.length > 0) {
            const userIds = selectedUsers.map((user) => user.id).join(';');
            orSpecs.push(`MultipleUserIdsSpecification:${userIds}`);
        }

        // If no targeting specifications, return empty data
        if (orSpecs.length === 0) {
            setDataList([]);
            setPageCount(0);
            return;
        }

        // Build AND specifications: column filters only
        const andSpecs = mapFiltersToSpecification(filters);
        const activeUserSpec = 'IsActiveSpecification:true';

        const data = await fetchUserList({
            pageIndex,
            pageSize,
            andSpecifications: andSpecs ? `${andSpecs},${activeUserSpec}` : activeUserSpec,
            orSpecifications: orSpecs.join(','),
        });

        if (data) {
            setDataList(data.elements);
            setPageCount(Math.ceil(data.total / pageSize));
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                color="inherit"
                startIcon={<EyeOutlined />}
                onClick={() => setDialogOpen(true)}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
            >
                Preview Recipients
            </Button>

            <Dialog
                maxWidth="xl"
                fullWidth
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                TransitionComponent={PopupTransition}
                sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
            >
                <DialogTitle
                    sx={{ m: 0, p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
                >
                    <div>
                        <Typography variant="h4">Preview Recipients (Read-only)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            This shows all users who will receive the message based on your groups and users selections
                        </Typography>
                    </div>
                    <IconButton
                        aria-label="close"
                        onClick={() => setDialogOpen(false)}
                        sx={{ color: 'text.secondary' }}
                    >
                        <CloseOutlined />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {dialogOpen && (
                        <ScrollX>
                            <BaseTable
                                key={refreshKey}
                                data={dataList}
                                hideColumnSelector
                                hideSearch
                                fetchData={fetchData}
                                pageCount={pageCount}
                                defaultColumnConfig={columnConfig}
                                defaultVisibleColumnConfig={DefaultVisibleUserColumns}
                            />
                        </ScrollX>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
export default MessageTargetPreviewTable;
